/**
 * MessageList — the one transcript orchestrator.
 *
 * VIEWER PERSPECTIVE (#907 T6). The same thread is read by two different
 * people. On the family surfaces the case manager is the OTHER party, so a
 * `trfer` turn wears TrferBubble's human identity. On the staff surfaces
 * (/trfer/threads/:id and the mobile handoff sheet) the viewer IS the case
 * manager: their own turns belong on the right, in the capsule that means
 * "you said this", and the FAMILY becomes the labelled left-hand speaker.
 *
 * `perspective` selects between the two. It defaults to 'family', so every
 * existing consumer (ChatPage, the widget, WilliamsPage) renders exactly as
 * before. Only the two handoff surfaces pass 'trfer'.
 *
 * The staff-side presenters live here rather than inside ChatBubble: they are
 * a property of WHO IS LOOKING, not of the turn itself, and ChatBubble's
 * anatomy (document assistant turn, capsule user turn) is unchanged by them.
 */
import { useRef, useEffect, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatBubble } from './ChatBubble';
import type { ChatDensity } from './ChatMarkdown';
import { TrferBubble } from '@/features/chat-trf/handoff/TrferBubble';
import { HandoffSystemEvent } from '@/features/chat-trf/handoff/HandoffSystemEvent';
import { SystemNote } from './SystemNote';
import { TypingIndicator } from './TypingIndicator';
import { ActionConfirmCard } from './ActionConfirmCard';
import { ThreadTranslationBanner } from '@/components/handoff/ThreadTranslationBanner';
import { TranslatedMessageBubble } from '@/components/handoff/TranslatedMessageBubble';
import { hasTranslationMetadata } from '@/lib/translation-utils';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { isToday, isYesterday, isSameDay } from 'date-fns';
import type { TFunction } from 'i18next';
import { formatPreset } from '@/i18n/intl';
import { formatClock } from '@/lib/chat-display';
import { AGENT_NAMES } from '@/lib/constants';
import { COMPANION_NAME } from '@/lib/companion-config';
import type { ChatMessageState, PendingAction } from '@/types/chat';
import type { ChatMessage } from '@/types/api';

const GROUP_THRESHOLD_MS = 2 * 60 * 1000;

function isGroupedWithPrev(messages: ChatMessageState[], index: number): boolean {
  if (index === 0) return false;
  const prev = messages[index - 1];
  const curr = messages[index];
  if (prev.role !== curr.role) return false;
  try {
    const prevTime = new Date(prev.timestamp).getTime();
    const currTime = new Date(curr.timestamp).getTime();
    return Math.abs(currTime - prevTime) < GROUP_THRESHOLD_MS;
  } catch { return false; }
}

/**
 * The day separator, in the reader's UI language (#2109 R4): "Today" and
 * "Yesterday" are catalog keys, any other day is the `longDate` Intl preset
 * ("September 30, 2026" in en, "30 de septiembre de 2026" in es).
 */
function getDateLabel(timestamp: string, t: TFunction<'chat'>): string {
  try {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    if (isToday(date)) return t('transcript.today');
    if (isYesterday(date)) return t('transcript.yesterday');
    return formatPreset(date, 'longDate');
  } catch { return ''; }
}

function DateSeparator({ label }: { label: string }) {
  if (!label) return null;
  return (
    <div className="flex items-center gap-3 my-3">
      <div className="flex-1 h-px bg-border" />
      <span className="text-caption text-stevens-ink-3 px-2 select-none">{label}</span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

/** Who is reading this transcript. See the file header. */
export type ChatPerspective = 'family' | 'trfer';

/**
 * The other party's turn as the staff viewer sees it: a left-hand capsule with
 * the speaker named above it. Deliberately quieter than ChatBubble's assistant
 * document turn — a family's message is a message, not a report — and quieter
 * than the primary capsule, which stays reserved for "you said this".
 */
function CounterpartTurn({
  message,
  label,
  showLabel,
  showTimestamp,
  density,
}: {
  message: ChatMessageState;
  label: string;
  showLabel: boolean;
  showTimestamp: boolean;
  density: ChatDensity;
}) {
  const time = message.timestamp ? formatClock(message.timestamp) : '';
  const isCompact = density === 'compact';
  return (
    <div
      role="group"
      aria-label={time ? `${label}, ${time}` : label}
      className="flex justify-start gap-3"
      data-testid="counterpart-turn"
    >
      <div className="flex flex-col items-start max-w-[80%] md:max-w-[70%]">
        {showLabel && (
          <span className={cn('text-caption font-medium text-stevens-ink-3', isCompact ? 'mb-0.5' : 'mb-1')}>
            {label}
          </span>
        )}
        <div
          className={cn(
            'rounded-lg border border-stevens-rule bg-stevens-bg-2 text-stevens-ink',
            isCompact ? 'px-3 py-2' : 'px-4 py-2.5'
          )}
        >
          <div className="whitespace-pre-wrap break-words text-body">{message.content}</div>
        </div>
        {showTimestamp && time && (
          <span className={cn('text-caption text-stevens-ink-3', isCompact ? 'mt-0.5' : 'mt-1')}>{time}</span>
        )}
      </div>
    </div>
  );
}

interface MessageListProps {
  messages: ChatMessageState[];
  isTyping: boolean;
  error: string | null;
  activeAgent?: string | null;
  personaName?: string;
  pendingAction?: PendingAction | null;
  isConfirming?: boolean;
  onRetry?: () => void;
  onDismissError?: () => void;
  onConfirmAction?: () => void;
  onCancelAction?: () => void;
  bookmarkedIds?: Set<string>;
  onBookmark?: (messageId: string) => void;
  /** Owning conversation, forwarded to the translated handoff bubble. */
  conversationId?: string;
  /**
   * Reading density, forwarded to every ChatBubble. Parent surfaces stay
   * editorial ('comfortable'); staff surfaces (Williams / ledger / handoff)
   * pass 'compact'. The list itself is unchanged by it.
   */
  density?: ChatDensity;
  /**
   * Handoff translation props. When provided and the thread contains
   * translated messages, renders the ThreadTranslationBanner at the top
   * and switches translated bubbles to TranslatedMessageBubble.
   *
   * handoffId: the active handoff ID (for per-message retranslate calls).
   * viewerLanguage: the current user's preferred language code (e.g. 'en', 'es').
   * otherPartyLanguage: the other party's preferred language (from the handoff record).
   * otherPartyName: display name of the other party for the banner.
   * disclosureMode: controls whether the full banner or chip-only disclosure is shown.
   */
  handoffId?: string;
  viewerLanguage?: string;
  otherPartyLanguage?: string;
  otherPartyName?: string;
  disclosureMode?: 'banner+chip' | 'chip-only';
  /**
   * Who is reading. 'family' (default) is every parent-facing surface and is
   * byte-for-byte the previous behaviour. 'trfer' flips the transcript for the
   * case manager: their own turns become the right-hand capsule, the family
   * becomes the labelled left-hand speaker.
   */
  perspective?: ChatPerspective;
  /** Name used for the family's turns under `perspective="trfer"`. */
  familyLabel?: string;
}

const SCROLL_THRESHOLD = 150;

export function MessageList({
  messages,
  isTyping,
  error,
  activeAgent,
  personaName = COMPANION_NAME,
  pendingAction,
  isConfirming,
  onRetry,
  onDismissError,
  onConfirmAction,
  onCancelAction,
  bookmarkedIds,
  onBookmark,
  conversationId,
  density = 'comfortable',
  handoffId,
  viewerLanguage = 'en',
  otherPartyLanguage = 'en',
  otherPartyName = 'your case manager',
  disclosureMode = 'banner+chip',
  perspective = 'family',
  familyLabel = 'Family',
}: MessageListProps) {
  const { t } = useTranslation('chat');
  const isTrferView = perspective === 'trfer';
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const hasInitiallyScrolledRef = useRef(false);

  const checkNearBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;
  }, []);

  const scrollToBottom = useCallback(() => {
    if (!isNearBottomRef.current) return;
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  useEffect(() => {
    if (messages.length > 0 && !hasInitiallyScrolledRef.current) {
      hasInitiallyScrolledRef.current = true;
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      });
      return;
    }
    scrollToBottom();
  }, [messages, isTyping, pendingAction, scrollToBottom]);

  const agentName = activeAgent ? AGENT_NAMES[activeAgent] ?? personaName : personaName;

  // True when any message in this thread carries translation metadata — used
  // to decide whether to mount the ThreadTranslationBanner.
  const hasAnyTranslatedMessage =
    !!handoffId && messages.some((m) => hasTranslationMetadata(m.metadata as Record<string, unknown> | undefined));

  return (
    <div
      ref={containerRef}
      className={density === 'compact' ? 'flex-1 overflow-y-auto p-3' : 'flex-1 overflow-y-auto p-3 sm:p-6'}
      onScroll={checkNearBottom}
      role="log"
      aria-live="polite"
    >
      {hasAnyTranslatedMessage && (
        <ThreadTranslationBanner
          viewerLanguage={viewerLanguage}
          otherPartyLanguage={otherPartyLanguage}
          otherPartyName={otherPartyName}
          disclosureMode={disclosureMode}
        />
      )}
      <div>
        {messages.map((message, index) => {
          const grouped = isGroupedWithPrev(messages, index);
          const isLastInGroup =
            index === messages.length - 1 ||
            !isGroupedWithPrev(messages, index + 1);
          const showDateSep =
            !!message.timestamp &&
            (index === 0 ||
              !isSameDay(
                new Date(messages[index - 1].timestamp),
                new Date(message.timestamp)
              ));

          // Handoff lifecycle system events (handback / reassign /
          // handoff_initiated / trfer_joined) render as the centred
          // italic-serif pill, not as a normal message bubble. Other 'system'
          // messages (escalation banners etc.) keep falling through to
          // ChatBubble.
          const eventKind = message.role === 'system' ? message.metadata?.event : undefined;
          const isHandoffSystemEvent =
            eventKind === 'handback' ||
            eventKind === 'reassign' ||
            eventKind === 'handoff_initiated' ||
            eventKind === 'trfer_joined';

          return (
            <Fragment key={message.id}>
              {showDateSep && <DateSeparator label={getDateLabel(message.timestamp, t)} />}
              <div className={grouped ? 'mt-0.5' : 'mt-4'}>
                {message.role === 'trfer' && !isTrferView ? (
                  <TrferBubble
                    content={message.content}
                    authorName={message.metadata?.trfer_name ?? t('handoff.caseManager')}
                    metadata={message.metadata as Record<string, unknown> | string | undefined}
                    viewerLanguage={viewerLanguage}
                  />
                ) : eventKind === 'receipt_sent' ? (
                  // #2379: a receipt sent from chat is a local note in both
                  // perspectives — never a companion turn with a bookmark on
                  // an id the server never stored.
                  <SystemNote content={message.content} decision="chat-receipt-thread-note" />
                ) : isHandoffSystemEvent ? (
                  <HandoffSystemEvent
                    kind={
                      eventKind === 'reassign' ? 'reassigned'
                        : eventKind === 'handoff_initiated' || eventKind === 'trfer_joined' ? 'joined'
                        : 'handback'
                    }
                    trferName={message.metadata?.to_trfer_name ?? message.metadata?.trfer_name}
                    companionName={personaName}
                  />
                ) : handoffId && hasTranslationMetadata(message.metadata as Record<string, unknown> | undefined) ? (
                  <TranslatedMessageBubble
                    message={message as unknown as ChatMessage}
                    viewerLanguage={viewerLanguage}
                    handoffId={handoffId}
                    conversationId={conversationId ?? ''}
                  />
                ) : isTrferView && message.role === 'trfer' ? (
                  // The viewer's own words. ChatBubble owns the capsule; the
                  // role is mapped rather than re-implemented so the two
                  // perspectives cannot drift apart visually.
                  <ChatBubble
                    message={{ ...message, role: 'user' }}
                    isGrouped={grouped}
                    showTimestamp={isLastInGroup}
                    personaName={personaName}
                    density={density}
                  />
                ) : isTrferView && message.role === 'user' ? (
                  <CounterpartTurn
                    message={message}
                    label={familyLabel}
                    showLabel={!grouped}
                    showTimestamp={isLastInGroup}
                    density={density}
                  />
                ) : isTrferView && message.role === 'system' ? (
                  <SystemNote content={message.content} />
                ) : (
                  <ChatBubble
                    message={message}
                    isGrouped={grouped}
                    showTimestamp={isLastInGroup}
                    personaName={personaName}
                    isBookmarked={bookmarkedIds?.has(message.id)}
                    onBookmark={onBookmark}
                    density={density}
                  />
                )}
              </div>
            </Fragment>
          );
        })}

        {pendingAction && onConfirmAction && onCancelAction && (
          <ActionConfirmCard
            action={pendingAction}
            onConfirm={onConfirmAction}
            onCancel={onCancelAction}
            isLoading={isConfirming}
          />
        )}

        {isTyping && <TypingIndicator agentName={agentName} />}

        {error && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-destructive/10 text-destructive text-body">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            {onRetry && (
              <Button variant="ghost" size="sm" onClick={onRetry} className="h-7 gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" />
                {t('errorRow.retry')}
              </Button>
            )}
            {onDismissError && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onDismissError}
                className="h-6 w-6 flex-shrink-0 text-destructive hover:text-destructive"
                aria-label={t('errorRow.dismiss')}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
